import {
  PAYMENT_STATUS,
  PAYMENT_STATUS_PENDING,
  SIMPLIFIED_PAYMENT_STATUS,
} from '../models/payment.models';

export enum PROJECT_STATE_VALUES {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  NEEDS_REVISION = 'needs-revision',
  REVISED = 'revised',
}

export const PROJECT_STATES = [
  { title: 'Entwurf', value: PROJECT_STATE_VALUES.DRAFT },
  { title: 'Eingereicht', value: PROJECT_STATE_VALUES.SUBMITTED },
  { title: 'Akzeptiert', value: PROJECT_STATE_VALUES.ACCEPTED },
  { title: 'Abgelehnt', value: PROJECT_STATE_VALUES.REJECTED },
  { title: 'Änderung nötig', value: PROJECT_STATE_VALUES.NEEDS_REVISION },
  { title: 'Nachgebessert', value: PROJECT_STATE_VALUES.REVISED },
] as const;

export const PROJECT_PAYMENT_STATUS = [
  {
    title: 'Zahlung in bearbeitung',
    value: SIMPLIFIED_PAYMENT_STATUS.PENDING,
    valueForDB: PAYMENT_STATUS_PENDING,
  },
  {
    title: 'Erfolgreich bezahlt',
    value: SIMPLIFIED_PAYMENT_STATUS.SUCCESS,
    valueForDB: PAYMENT_STATUS.SUCCEEDED,
  },
  {
    title: 'Zahlung Abgelehnt',
    value: SIMPLIFIED_PAYMENT_STATUS.FAILED,
    valueForDB: PAYMENT_STATUS.REQUIRES_PAYMENT_METHOD,
  },
] as const;

export const EDITING_ALLOWED_PROJECT_STATES: PROJECT_STATE_VALUES[] = [
  PROJECT_STATE_VALUES.DRAFT,
  PROJECT_STATE_VALUES.NEEDS_REVISION,
];

export const PROJECT_FIELD_TYPES = [
  'option',
  'text',
  'testimonial',
  'file',
] as const;

export enum PROJECT_FILE_TYPE_VALUES {
  AUDIO = 'audio',
  VIDEO = 'video',
  IMAGE = 'image',
  DOCUMENT = 'document',
  IMAGE_ORIGINALS = 'image-originals',
}

export enum PROJECT_PAYMENT_TYPE_VALUES {
  FULL = 'full',
  REDUCED = 'reduced',
}

export const PROJECT_PAYMENT_TYPES = [
  PROJECT_PAYMENT_TYPE_VALUES.FULL,
  PROJECT_PAYMENT_TYPE_VALUES.REDUCED,
] as const;

export const PROJECT_REVISION_REASONS = [
  {
    title: 'Copyright sichtbar (Bild)',
    snippet:
      'Leider ist auf einem oder mehreren Bildern ein Copyright-Hinweis sichtbar. Für eine anonyme Bewertung bitten wir Dich, das Logo oder Wasserzeichen zu entfernen und die Bilder erneut hochzuladen.',
    value: 'copyrightImage',
  },
  {
    title: 'Copyright sichtbar (Video)',
    snippet:
      'Leider ist in Deinem eingereichten Video ein Copyright oder Branding sichtbar. Bitte reiche ein neutrales, nicht gebrandetes Video ein, damit eine faire und anonyme Bewertung durch die Jury gewährleistet ist.',
    value: 'copyrightVideo',
  },
  {
    title: 'Audio nicht anonym',
    snippet:
      'In Deiner Audio-Datei ist Dein Firmenname oder ein anderer identifizierbarer Hinweis enthalten. Bitte ersetze das Audio durch eine anonyme Version, um die Jurybewertung nicht zu beeinflussen.',
    value: 'audioNotAnonymous',
  },
  {
    title: 'Ort/Datum unvollständig',
    snippet:
      'Deine Angaben zum Hochzeitsort bzw. zum Hochzeitsdatum scheinen nicht vollständig oder nicht korrekt zu sein. Bitte kontrolliere diese nochmals. Es können nur Projekte aus Österreich aus der letzten Hochzeitssaison eingereicht werden.',
    value: 'locationDateIncomplete',
  },
  {
    title: 'Text nicht anonymisiert',
    snippet:
      'Deine Projektbeschreibung oder Referenz enthält persönliche Angaben oder einen Hinweis auf Deine Firma. Bitte formuliere den Text neutral und anonym, damit die Jury unvoreingenommen bewerten kann.',
    value: 'textNotAnonymized',
  },
  {
    title: 'Hochzeit nicht in Österreich',
    snippet:
      'Der Aufnahmeort liegt außerhalb Österreichs. Bitte beachte, dass nur Shootings und Hochzeiten innerhalb Österreichs berücksichtigt werdne können für eine Einreichung.',
    value: 'weddingNotInAustria',
  },
  {
    title: 'Firmensitz nicht in Österreich',
    snippet:
      'Es scheint, dass du keinen österreichischen Unternehmenssitz hast. Bitte beachte, dass nur Unternehmen mit Sitz in Österreich einreichen dürfen.',
    value: 'companyNotInAustria',
  },
  {
    title: 'Musiklizenz fehlt',
    snippet:
      'Bitte reiche die verwendete Musiklizenz nach – entweder als Downloadlink oder mit einer klaren Quellenangabe. Ohne Nachweis kann die Einreichung leider nicht freigegeben werden.',
    value: 'musicLicenseMissing',
  },
  {
    title: 'Styled Shoot: Dienstleister fehlen',
    snippet:
      'Bei Styled Shootings müssen zumindest 3 beteiligte Dienstleister*innen genannt werden. Diese Informationen sind wichtig für die finale Darstellung und müssen nachgereicht werden. Natürlich werden die Namen NICHT an die Jury weitergegeben um eine objektive Bewertung zu gewährleisten.',
    value: 'styledShootVendorsMissing',
  },
  {
    title: 'Bildqualität unzureichend',
    snippet:
      'Einige Deiner hochgeladenen Bilder sind unscharf oder haben eine sehr geringe Auflösung. Für eine faire Beurteilung bitten wir Dich, qualitativ hochwertige Bilder nachzureichen.',
    value: 'poorImageQuality',
  },
  {
    title: 'Kategorie nicht passend',
    snippet:
      'Dein Projekt scheint nicht ganz zur gewählten Einreich-Kategorie zu passen. Bitte überprüfe nochmals die Definitionen der Kategorien – ein Wechsel könnte dein Projekt in einem ganz neuen Licht erstrahlen lassen. Hinweis: Mit Wechsel der Kategorie gehen alle Eingaben verloren. Es empfiehlt sich die Texte vorab in einem Wordfile zwischenzuspeichern.',
    value: 'categoryMismatch',
  },
  {
    title: 'Video zu lang/groß',
    snippet:
      'Das hochgeladene Video überschreitet die erlaubte Länge oder Dateigröße. Bitte kürze oder komprimiere es entsprechend den Kategoriekriterien und lade es erneut hoch.',
    value: 'videoTooLarge',
  },
  {
    title: 'Testimonial enthält Namen',
    snippet:
      'In der Referenz/Testimonial ist dein Name und/oder dein Firmenname enthalten. Für die anonyme Bewertung bitten wir Dich, diese Stellen zu neutralisieren.',
    value: 'testimonialContainsNames',
  },
  {
    title: 'Dokumente fehlen',
    snippet:
      'Einige für die Kategorie erforderlichen Informationen (z. B. Lizenznachweis, Moodboard, PDF-Dokument) fehlen. Bitte reiche diese nach, damit Dein Projekt vollständig ist.',
    value: 'documentsMissing',
  },
  {
    title: 'Upload unvollständig',
    snippet:
      'Der Uploadprozess scheint unterbrochen worden zu sein – es fehlen Dateien oder Angaben. Bitte kontrolliere Deine Einreichung und vervollständige sie gegebenenfalls.',
    value: 'uploadIncomplete',
  },
  {
    title: 'Nicht autorisierte Inhalte',
    snippet:
      'Dein Projekt enthält möglicherweise Inhalte, für die Du nicht die nötigen Nutzungsrechte besitzt. Bitte stelle sicher, dass Du zur Verwendung aller eingereichten Materialien berechtigt bist.',
    value: 'unauthorizedContent',
  },
  {
    title: 'Duplicate Submission',
    snippet:
      'Das Projekt scheint versehentlich doppelt eingereicht worden zu sein. Bitte überprüfe Deine Übersicht und lösche gegebenenfalls eine Version, damit es nicht zu Verwirrungen kommt.',
    value: 'duplicateSubmission',
  },
  {
    title: 'Unklare Storyline / Beschreibung',
    snippet:
      'Die Projektbeschreibung wirkt aktuell noch etwas unklar oder unstrukturiert. Um der Jury das Verständnis zu erleichtern, wäre eine überarbeitete, klarere Darstellung hilfreich.',
    value: 'unclearStoryline',
  },
];
